import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserConsentInput } from '../apis/UserConsentsApi/UserConsentsApi.ts';
import { useSessionProvider } from '../provider/sessionContext.tsx';
import { UserConsentData, CookieConsentService } from '../services/userConsentService.ts';
import { ThirdPartyScriptManager } from '../services/thirdPartyScriptManager.ts';
import { useGetUserPreferences } from '../entities/UserConsent/queries/useGetUserPreferences.tsx';
import { useAcceptMMEWarning } from '../entities/UserConsent/mutations/useAcceptMMEWarning.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../queryDefaults/QueryKeys.ts';

export interface UseUserConsentReturn {
    consent: UserConsentData | null;
    hasConsent: (category: 'essential' | 'analytics') => boolean;
    needsConsent: boolean;
    saveConsent: (consent: UserConsentInput) => Promise<void>;
    revokeConsent: () => Promise<void>;
    isLoading: boolean;
    hasAcceptedMMEWarning: boolean;
    acceptMMEWarning: () => Promise<void>;
}

export function useUserConsent(): UseUserConsentReturn {
    const { currentUser } = useSessionProvider();
    const queryClient = useQueryClient();
    const [consent, setConsent] = useState<UserConsentData | null>(null);
    const [consentLoading, setConsentLoading] = useState(true);
    
    // Use React Query to fetch user preferences with caching
    const { 
        data: userPreferences, 
        isLoading: isLoadingPreferences, 
    } = useGetUserPreferences(!!currentUser?.hasProfile);
    
    const acceptMMEWarningMutation = useAcceptMMEWarning();

    // Sync cookie with React Query preferences data
    useEffect(() => {
        if (!currentUser?.hasProfile) {
            // User not authenticated, delete cookie if exists
            const localConsent = CookieConsentService.getConsent();
            if (localConsent) {
                Cookies.remove('cookieConsent');
                setConsent(null);
            }
            setConsentLoading(false);
            return;
        }

        // If still loading preferences, keep loading state
        if (isLoadingPreferences) {
            setConsentLoading(true);
            return;
        }

        // User is authenticated and preferences are loaded
        if (userPreferences) {
            if (userPreferences.hasConsent && userPreferences.consentDate) {
                const isExpired = CookieConsentService.isConsentExpired(userPreferences.consentDate);

                if (!isExpired) {
                    // Update cookie from preferences data
                    const cookieData: UserConsentData = {
                        essentialCookies: userPreferences.essentialCookies,
                        analyticsCookies: userPreferences.analyticsCookies,
                        consentDate: userPreferences.consentDate,
                        consentVersion: userPreferences.consentVersion || '1.0',
                    };

                    Cookies.set('cookieConsent', JSON.stringify(cookieData), {
                        expires: 365,
                        sameSite: 'strict',
                    });
                    setConsent(cookieData);
                } else {
                    // Consent expired, remove cookie
                    Cookies.remove('cookieConsent');
                    setConsent(null);
                }
            } else {
                // No consent in database, remove cookie
                Cookies.remove('cookieConsent');
                setConsent(null);
            }
        }

        setConsentLoading(false);
    }, [currentUser?.hasProfile, userPreferences, isLoadingPreferences]);

    const hasConsent = useCallback((category: 'essential' | 'analytics') => {
        return CookieConsentService.hasConsent(category);
    }, []);

    const saveConsent = useCallback(
        async (consentData: UserConsentInput) => {
            if (!currentUser?.hasProfile)
                throw new Error('User must be authenticated to save consent');

            await CookieConsentService.saveConsent(consentData);
            // Invalidate React Query cache to refetch preferences
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.userPreferences] });
            // Cookie is set synchronously by the service, no need to wait
            const updatedConsent = CookieConsentService.getConsent();
            setConsent(updatedConsent);
            // Reload third-party scripts based on new consent
            ThirdPartyScriptManager.reloadScripts();
        },
        [currentUser?.hasProfile, queryClient]
    );

    const revokeConsent = useCallback(async () => {
        if (!currentUser?.hasProfile)
            throw new Error('User must be authenticated to revoke consent');

        await CookieConsentService.revokeConsent();
        // Invalidate React Query cache to refetch preferences
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.userPreferences] });
        setConsent(null);
        // Reload scripts after revoking consent
        ThirdPartyScriptManager.reloadScripts();
    }, [currentUser?.hasProfile, queryClient]);

    const acceptMMEWarning = useCallback(async () => {
        if (!currentUser?.hasProfile)
            throw new Error('User must be authenticated to accept MME warning');

        await acceptMMEWarningMutation.mutateAsync();
    }, [currentUser?.hasProfile, acceptMMEWarningMutation]);

    // Recalculate needsConsent based on current consent state
    // If we have consent in state, we don't need consent (unless expired)
    const needsConsent = useMemo(() => {
        // If we have consent in state and it's not expired, we don't need consent
        if (consent && !CookieConsentService.isConsentExpired(consent.consentDate))
            return false;
        // Otherwise check the service
        return CookieConsentService.needsConsent();
    }, [consent]);

    // Combine loading states
    const isLoading = consentLoading || isLoadingPreferences;
    
    // Get MME warning acceptance status from React Query data
    const hasAcceptedMMEWarning = userPreferences?.mmeCalculatorWarningAccepted ?? false;

    return {
        consent,
        hasConsent,
        needsConsent,
        saveConsent,
        revokeConsent,
        isLoading,
        hasAcceptedMMEWarning,
        acceptMMEWarning,
    };
}
